import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Button, Input, Panel } from "@realtyflows/ui";
import { forgotPassword } from "../api";
import Alert from "../components/Alert";
import useResponsive from "../hooks/useResponsive";
import useScrollToAlert from "../hooks/useScrollToError";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;
  const { isCompact } = responsive;

  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const errorRef = useScrollToAlert(error);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email) { setError("Please enter your email"); return; }

    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: t.pageBg, padding: isMobile ? "16px" : "20px" }}>
      <div className="rf-a1" style={{ width: isMobile ? "100%" : 420, maxWidth: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 20 : 32 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <img src="/logo.png" alt="RealtyFlows" style={{ width: 28, height: 28, borderRadius: 4 }} />
            <span style={{ fontSize: isMobile ? 16 : 18, fontWeight: 600, color: t.textPrimary, fontFamily: font.display }}>
              Realty<span style={{ color: t.gold }}>Flows</span>
            </span>
          </div>
          <div style={{ fontSize: isMobile ? 13 : 14, color: t.textSecondary, fontFamily: font.body }}>Reset your password</div>
        </div>

        <Panel padding={isMobile ? 20 : isCompact ? 24 : 32}>
          {sent ? (
            <div style={{ textAlign: "center", padding: isMobile ? "12px 0" : "20px 0" }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: t.emeraldLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 20, color: t.emerald }}>&#10003;</div>
              <div style={{ fontSize: isMobile ? 15 : 16, fontWeight: 700, color: t.textPrimary, fontFamily: font.body, marginBottom: 8 }}>Check your email</div>
              <div style={{ fontSize: 13, color: t.textSecondary, fontFamily: font.body, lineHeight: 1.6, marginBottom: 20 }}>
                If an account exists for <strong>{email}</strong>, we've sent a password reset link. The link expires in 1 hour.
              </div>
              <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body, marginBottom: 16 }}>
                Didn't receive it? Check your spam folder or try again.
              </div>
              <Button variant="outline" size="sm" onClick={() => setSent(false)} fullWidth={isMobile}>Try Again</Button>
            </div>
          ) : (
            <>
              <Alert ref={errorRef} message={error} onDismiss={() => setError("")} style={{ marginBottom: 16 }} />

              <div style={{ fontSize: 13, color: t.textSecondary, fontFamily: font.body, marginBottom: isMobile ? 16 : 20, lineHeight: 1.6 }}>
                Enter the email address associated with your account and we'll send you a link to reset your password.
              </div>

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: isMobile ? 16 : 20 }}>
                  <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="alex@realtyflows.io" icon="✉" />
                </div>
                <Button variant="primary" size="lg" fullWidth type="submit" disabled={loading}>
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
              </form>
            </>
          )}
        </Panel>

        <div style={{ textAlign: "center", marginTop: isMobile ? 16 : 20, fontSize: 13, color: t.textSecondary, fontFamily: font.body }}>
          Remember your password?{" "}
          <span onClick={() => navigate("/login")} style={{ color: t.emerald, fontWeight: 600, cursor: "pointer" }}>Sign in</span>
        </div>
      </div>
    </div>
  );
}
